import React from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Icon, Text } from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import Button from '@/components/Button';
import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import Points from '@/components/Points';
import useTiers from '@/hooks/useTiers';
import { SCREEN_SIZE } from '@/lib/theme';
import { Charges } from '@/v2/services/availability/types/api/parsedResponse';
import Price from './components/Price';
import {
  getPointsDifference,
  getPointsDifferenceStaticCash,
} from './helpers/getPointsDifference';
import getPriceDifference from './helpers/getPriceDifference';

const Container = styled(Flex)`
  border-width: 0;
  border-style: solid;
  border-color: ${themeGet('dividers.horizontal.backgroundColor')};
  border-top-width: 1px;

  ${SCREEN_SIZE.MD} {
    border-top-width: 0;
    border-inline-start-width: 1px;
  }
`;

const SelectedText = styled(Text)`
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;

export interface ChargeOptions {
  selectedOption: Charges;
  option: Charges;
  offer?: {
    total: Charges['total'];
  };
  current: {
    amount: string | number;
    fraction: number;
  };
}

export interface FlightCTAProps {
  isSelected?: boolean;
  charges: ChargeOptions;
  onSelected: () => void;
}

const FlightCTA = React.memo(
  ({ isSelected = false, charges, onSelected }: FlightCTAProps) => {
    const tiers = useTiers();
    const perTravellerPriceDifference = getPriceDifference(charges);
    const { usePoints, usePointsPlusPay } = useCustomerSelection();

    if (isSelected) {
      return (
        <Container
          data-testid="FLIGHT-SEGMENT-CTA"
          justifyContent={['center', null, 'flex-end']}
          alignItems="flex-start"
          pt={['4', null, '0']}
          gap="2"
          color="brand.primary"
        >
          <Icon name="done" aria-hidden="true" />
          <SelectedText fontSize="base" fontWeight="bold">
            Selected
          </SelectedText>
        </Container>
      );
    }

    const pointsConversion = usePointsPlusPay
      ? getPointsDifferenceStaticCash
      : getPointsDifference;

    return (
      <Container
        data-testid="FLIGHT-SEGMENT-CTA"
        justifyContent={['space-between', null, 'flex-end']}
        alignItems="flex-start"
        pt={['4', null, '0']}
      >
        <Flex
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          flexGrow={[1, null, 0]}
          gap={6}
        >
          <Flex alignItems="baseline" data-testid="FLIGHT_PRICE_CONTAINER">
            <Box fontSize="xl" fontWeight="bold">
              {perTravellerPriceDifference.sign}
            </Box>
            {usePoints ? (
              <Points
                points={pointsConversion({
                  charges,
                  tiers,
                })}
              />
            ) : (
              <Price perTraveller={perTravellerPriceDifference} />
            )}
          </Flex>
          <Box width="130px" ml={[null, null, 'auto']}>
            <Button
              data-testid="FLIGHT_SELECT_BUTTON"
              // @ts-expect-error outline is valid but not inferred correctly
              outline
              variant="primary"
              width="100%"
              onClick={(event) => {
                event.preventDefault();
                onSelected();
              }}
            >
              Select
            </Button>
          </Box>
        </Flex>
      </Container>
    );
  },
);

export default FlightCTA;
